import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CalendarDays, Check, CheckCircle, ChevronDown, FileText, MoreHorizontal, Plus, Search, SquarePen, Trash2, X } from 'lucide-react';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { getCouponService } from '../../../domains/coupon';
import useAuthStore from '../../../core/stores/authStore';
import {
  useCouponTemplates, useCouponConditions, useRecommendedTargetGroups, useTargetGroups,
  useSaveTargetGroup, useDeleteTargetGroup, useCreateCouponTemplate, useDeleteCouponTemplate,
} from '../../hooks/coupon/useCouponTargeting';
import {
  formatAmount, toApiGender, fromApiGender, genderLabel, agesToApi, agesToLabels,
  buildEligibleParams, periodDays, savedGroupDetail,
} from '../../hooks/coupon/couponTargeting';
import { useMyProducts } from '../../hooks/ranking/useInsightRanking';
import useUIStore from '../../../core/stores/uiStore';
import styles from './CouponDistribute.module.css';

const AGE_MAP = {
  '전체': [],
  '20대': [20],
  '30대': [30],
  '40대': [40],
  '50대': [50],
  '60대+': [60, 70, 80, 90],
};

const toDateString = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const MODAL_DISCOUNT_LABEL = { FIXED: '고정금액 (원)', PERCENT: '퍼센트 (%)' };

const getTodayString = () => toDateString(new Date());

const getNextMonthString = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

// 상품 목록 = GET /insight/my-products (우리 약국 판매 상품). 단가·재고 수량은 데이터 없음 → '-'
// shortcut: 추천 묶음 카드(급상승·함께 구매·신규 발주)는 데이터가 없어 개수 '-', 눌러도 상품을 미리 고르지 않음 — 기획 결정 후 반영

const THIS_MONTH = `${new Date().getMonth() + 1}월`;

const CouponDistribute = () => {
  const navigate = useNavigate();
  const pharmacy = useAuthStore(state => state.pharmacy);
  const setHeaderTabs = useUIStore(state => state.setHeaderTabs);

  // 섹션1: 쿠폰 설정
  const [couponName, setCouponName] = useState(`${THIS_MONTH} 신상품 프로모션`);
  const [description, setDescription] = useState('이번 달 떠오르는 인기 건기식을 10% 할인 받으세요!');
  const [discountType, setDiscountType] = useState('FIXED');
  const [amount, setAmount] = useState('3000');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateMenuId, setTemplateMenuId] = useState(null);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [modalDropdown, setModalDropdown] = useState(null);
  // 추천 타겟 key. null 이면 '직접 필터로 설정'(modalDirect) 또는 미선택
  const [modalTargetKey, setModalTargetKey] = useState(null);
  const [modalDirect, setModalDirect] = useState(false);
  const [modalDiscount, setModalDiscount] = useState('FIXED');
  const [modalDiscountValue, setModalDiscountValue] = useState('');
  const [modalPeriod, setModalPeriod] = useState('30');
  const [modalGuide, setModalGuide] = useState('');
  // 적용 상품 — POST /coupon/templates product_ids (바코드, 백엔드 10/23), 검색은 GET /insight/my-products
  const [modalProducts, setModalProducts] = useState([]);
  const [modalProductSearch, setModalProductSearch] = useState('');
  const [modalProductQuery, setModalProductQuery] = useState('');

  // API: 템플릿 · 처방 질병 · 추천 타겟 · 저장 그룹
  const { data: templates = [] } = useCouponTemplates();
  const { data: conditionGroups = [] } = useCouponConditions();
  const { data: recommendedTargets = [] } = useRecommendedTargetGroups();
  const { data: savedGroups = [] } = useTargetGroups();
  const saveTargetGroup = useSaveTargetGroup();
  const deleteTargetGroup = useDeleteTargetGroup();
  const createTemplate = useCreateCouponTemplate();
  const deleteTemplate = useDeleteCouponTemplate();

  // 섹션2: 쿠폰 유효기간
  const [startDate, setStartDate] = useState(getTodayString());
  const [expiryDate, setExpiryDate] = useState(getNextMonthString());
  const [isDateCalendarOpen, setIsDateCalendarOpen] = useState(false);

  // 섹션3: 타겟 그룹
  const [targetMode, setTargetMode] = useState('new');
  const [gender, setGender] = useState('전체');
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState([]);
  // 추천 타겟은 key(string), 저장 그룹은 id(number)
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [editingSavedGroup, setEditingSavedGroup] = useState(false);
  const [savedMenuId, setSavedMenuId] = useState(null);

  // 상품 선택
  const [selectedProducts, setSelectedProducts] = useState([]); // barcode 목록
  const [productMode, setProductMode] = useState('trending');
  const [productSearch, setProductSearch] = useState('');
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);

  // 타겟수 (API 연동)
  // eligible-count 응답 data (count + E-4 프로파일). 실패/미조회 시 null
  const [eligible, setEligible] = useState(null);

  // 완료
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 쿠폰 이벤트 / 포인트 이벤트 헤더 탭 숨김 (디자인 QA 10/23) — 다른 화면이 남긴 탭이 없도록 비움만
  useEffect(() => {
    setHeaderTabs(null);
  }, [setHeaderTabs]);

  // 추천 타겟 로드 후 첫 카드 선택 (4f7c1c7/Figma: 첫 카드 선택 상태)
  useEffect(() => {
    if (targetMode === 'new' && selectedGroupId == null && recommendedTargets.length) setSelectedGroupId(recommendedTargets[0].key);
  }, [targetMode, selectedGroupId, recommendedTargets]);

  const isFormValid = couponName?.trim() && Number(amount) >= 1 && (discountType !== 'PERCENT' || Number(amount) <= 100) && expiryDate;

  const discountTypeLabel = { FIXED: '고정금액', PERCENT: '퍼센트' };

  const AGE_OPTIONS = ['전체', '20대', '30대', '40대', '50대', '60대+'];

  const handleAgeToggle = (age) => {
    if (age === '전체') {
      setSelectedAges([]);
      return;
    }
    setSelectedAges(prev =>
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleProductToggle = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleConditionToggle = (condition) => {
    setSelectedConditions(prev =>
      prev.includes(condition) ? prev.filter(item => item !== condition) : [...prev, condition]
    );
  };

  // 글자마다 API 를 부르지 않게 입력이 300ms 멈추면 조회
  const [productQuery, setProductQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setProductQuery(productSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [productSearch]);
  const { data: myProducts } = useMyProducts(productQuery);
  useEffect(() => {
    const t = setTimeout(() => setModalProductQuery(modalProductSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [modalProductSearch]);
  const { data: modalProductData } = useMyProducts(modalProductQuery);
  const modalProductResults = (modalProductData?.list ?? []).slice(0, 5);
  const filteredProducts = useMemo(() => (myProducts?.list ?? []).map(p => ({
    id: p.barcode,
    name: p.name,
    summaryName: p.name,
    category: [p.manufacturer, p.product_type].filter(Boolean).join(' · '),
    monthly: p.sales_qty_30d,
  })), [myProducts]);
  // 검색어가 바뀌어도 이미 고른 상품 정보가 남도록 한 번 본 상품은 기억
  const seenProducts = useRef({});
  filteredProducts.forEach(p => { seenProducts.current[p.id] = p; });
  modalProductResults.forEach(p => { seenProducts.current[p.barcode] ??= { id: p.barcode, name: p.name, summaryName: p.name, category: [p.manufacturer, p.product_type].filter(Boolean).join(' · '), monthly: p.sales_qty_30d }; });
  const visibleProducts = filteredProducts;

  // 처방 질병 코드 → 이름 (GET /coupon/conditions)
  const conditionNames = useMemo(() => {
    const names = {};
    conditionGroups.forEach(group => (group.items || []).forEach(item => { names[item.code] = item.name || item.code; }));
    return names;
  }, [conditionGroups]);
  const conditionText = (t) => [
    ...(t?.target_conditions || []).map(code => conditionNames[code] || code),
    t?.last_visit_days_gte ? `마지막 방문 ${t.last_visit_days_gte}일+` : null,
  ].filter(Boolean).join(' · ');

  const selectedTarget = targetMode === 'saved'
    ? savedGroups.find(group => group.id === selectedGroupId) || null
    : targetMode === 'new'
      ? recommendedTargets.find(target => target.key === selectedGroupId) || null
      : null;

  // 현재 타겟 → API 필드 {target_gender, target_ages, target_conditions, last_visit_days_gte}
  const directTarget = {
    target_gender: toApiGender(gender),
    target_ages: agesToApi(selectedAges, AGE_MAP),
    target_conditions: selectedConditions,
  };
  const currentTarget = targetMode === 'direct'
    ? directTarget
    : targetMode === 'saved'
      ? (editingSavedGroup ? { ...directTarget, last_visit_days_gte: selectedTarget?.last_visit_days_gte } : selectedTarget)
      : selectedTarget?.condition;
  const eligibleParamsKey = JSON.stringify(buildEligibleParams(currentTarget || {}));

  // eligible-count API 호출 (디바운스 300ms). 질병·미방문 조건과 프로파일은 apiClient 가 붙이는 약국 토큰이 있어야 적용됨
  useEffect(() => {
    const brn = pharmacy?.business_registration_number;
    if (!brn) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await getCouponService().getEligibleCount(brn, JSON.parse(eligibleParamsKey));
        if (!cancelled) setEligible(res?.result_code === '200' ? res.data : null);
      } catch {
        if (!cancelled) setEligible(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pharmacy, eligibleParamsKey]);

  const templateDiscount = (t) => (t.discount_type === 'PERCENT' ? `${t.discount_value}% 퍼센트` : `${formatAmount(t.discount_value)}원 고정`);
  const templateTarget = (t) => {
    const group = savedGroups.find(g => g.id === t.target_group_id);
    if (!group) return null;
    return group.count != null ? `${group.name} · ${group.count}명` : group.name;
  };

  const applyTemplate = (t) => {
    setSelectedTemplate(t.id);
    setDiscountType(t.discount_type);
    setAmount(String(t.discount_value));
    if (t.guide_text) setDescription(t.guide_text);
    if (t.product_ids?.length) setSelectedProducts(t.product_ids);
    if (t.default_period_days) {
      const end = new Date(`${startDate}T00:00:00`);
      end.setDate(end.getDate() + t.default_period_days);
      setExpiryDate(toDateString(end));
    }
    if (t.target_group_id && savedGroups.some(g => g.id === t.target_group_id)) {
      setTargetMode('saved');
      setEditingSavedGroup(false);
      setSelectedGroupId(t.target_group_id);
    }
  };

  const handleDeleteTemplate = (id) => {
    setTemplateMenuId(null);
    deleteTemplate.mutate(id, {
      onSuccess: () => { if (selectedTemplate === id) setSelectedTemplate(null); },
      onError: (error) => alert(error?.message || '템플릿 삭제에 실패했습니다.'),
    });
  };

  const handleDeleteSavedGroup = (id) => {
    setSavedMenuId(null);
    deleteTargetGroup.mutate(id, {
      onSuccess: () => { if (selectedGroupId === id) { setSelectedGroupId(null); setEditingSavedGroup(false); } },
      onError: (error) => alert(error?.message || '저장 그룹 삭제에 실패했습니다.'),
    });
  };

  const startEditSavedGroup = (group) => {
    setSelectedGroupId(group.id);
    setGender(fromApiGender(group.target_gender));
    setSelectedAges(agesToLabels(group.target_ages, AGE_MAP));
    setSelectedConditions(group.target_conditions || []);
    setSavedMenuId(null);
    setEditingSavedGroup(true);
  };

  const handleSaveEditedGroup = async () => {
    if (!selectedTarget) return;
    try {
      await saveTargetGroup.mutateAsync({
        id: selectedTarget.id,
        name: selectedTarget.name,
        detail: selectedTarget.detail,
        ...directTarget,
        last_visit_days_gte: selectedTarget.last_visit_days_gte,
      });
      setEditingSavedGroup(false);
    } catch (error) {
      console.error('저장 그룹 수정 실패:', error);
      alert(error?.message || '저장 그룹 수정에 실패했습니다.');
    }
  };

  const resetTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setModalDropdown(null);
    setTemplateName('');
    setModalTargetKey(null);
    setModalDirect(false);
    setModalDiscount('FIXED');
    setModalDiscountValue('');
    setModalPeriod('30');
    setModalGuide('');
    setModalProducts([]);
    setModalProductSearch('');
  };

  const isModalValid = templateName.trim() && Number(modalDiscountValue) >= 1 && (modalDiscount !== 'PERCENT' || Number(modalDiscountValue) <= 100);

  const handleCreateTemplate = async () => {
    try {
      await createTemplate.mutateAsync({
        name: templateName.trim(),
        // ponytail: 템플릿은 저장 그룹 id 만 받음 → 추천 타겟 조건은 저장 안 됨 (백엔드 필드 필요)
        target_group_id: null,
        discount_type: modalDiscount,
        discount_value: Number(modalDiscountValue),
        default_period_days: Number(modalPeriod) || null,
        guide_text: modalGuide.trim() || null,
        product_ids: modalProducts,
      });
      resetTemplateModal();
    } catch (error) {
      console.error('템플릿 저장 실패:', error);
      alert(error?.message || '템플릿 저장에 실패했습니다.');
    }
  };

  const handleSubmit = async () => {
    if (!couponName?.trim()) {
      alert('쿠폰 이름을 입력해주세요.');
      return;
    }
    if (!amount || Number(amount) < 1) {
      alert('할인 금액을 입력해주세요.');
      return;
    }
    if (discountType === 'PERCENT' && Number(amount) > 100) {
      alert('할인율은 100%를 초과할 수 없습니다.');
      return;
    }
    if (!expiryDate) {
      alert('만료일을 선택해주세요.');
      return;
    }
    try {
      setIsSubmitting(true);
      const couponService = getCouponService();

      // 요청 바디 형태는 그대로 (질병·상품 조건 저장은 E-7 대기)
      const couponData = {
        pharmacy_id: pharmacy?.id,
        coupon_type: 'DISCOUNT',
        discount_type: discountType,
        amount: Number(amount),
        title: couponName || undefined,
        description: description || undefined,
        scope_type: 'PHARMACY',
        expiry_date: expiryDate || undefined,
        target_gender: currentTarget?.target_gender || 'ALL',
        target_ages: currentTarget?.target_ages?.length ? currentTarget.target_ages : null,
      };

      const res = await couponService.createCoupon(couponData);
      // 실패도 HTTP 200 + result_code 로 온다 → 확인 안 하면 실패인데 완료 화면이 뜬다
      if (res?.result_code !== '200' && res?.resultCode !== 200) throw new Error(res?.result_msg || '쿠폰 지급에 실패했습니다.');

      if (saveTemplate && saveTemplateName.trim()) {
        try {
          await createTemplate.mutateAsync({
            name: saveTemplateName.trim(),
            target_group_id: targetMode === 'saved' ? selectedGroupId : null,
            discount_type: discountType,
            discount_value: Number(amount),
            default_period_days: periodDays(startDate, expiryDate),
            guide_text: description?.trim() || null,
            product_ids: selectedProducts,
          });
        } catch (error) {
          console.error('템플릿 저장 실패:', error);
          alert('쿠폰은 지급됐지만 템플릿 저장에 실패했습니다.');
        }
      }
      setIsCompleted(true);
    } catch (error) {
      console.error('쿠폰 지급 실패:', error);
      alert(error?.message || '쿠폰 지급에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setCouponName(`${THIS_MONTH} 신상품 프로모션`);
    setDescription('이번 달 떠오르는 인기 건기식을 10% 할인 받으세요!');
    setDiscountType('FIXED');
    setAmount('3000');
    setSelectedTemplate(null);
    setSaveTemplate(false);
    setSaveTemplateName('');
    setStartDate(getTodayString());
    setExpiryDate(getNextMonthString());
    setTargetMode('new');
    setGender('전체');
    setSelectedAges([]);
    setSelectedConditions([]);
    setSelectedGroupId(null);
    setSelectedProducts([]);
    setProductSearch('');
    setIsCompleted(false);
  };

  const selectedProductItems = selectedProducts.map(id => seenProducts.current[id]).filter(Boolean);

  // E-4 타겟 프로파일 — null 인 항목은 숨김
  const profileAge = [
    eligible?.avg_age != null ? `평균 ${eligible.avg_age}세` : null,
    eligible?.dominant_gender ? genderLabel(eligible.dominant_gender) : null,
  ].filter(Boolean).join(' ');
  const profileTicket = eligible?.avg_ticket != null ? `평균 객단가 ${formatAmount(eligible.avg_ticket)}원` : '';
  const modalTarget = recommendedTargets.find(t => t.key === modalTargetKey);
  const targetOptionLabel = (t) => (t.count != null ? `${t.name} (${t.count}명)` : t.name);
  const activeTemplateId = selectedTemplate ?? templates[0]?.id;

  return (
    <div className={styles.container}>
      {isCompleted ? (
        <div className={styles.completeWrap}>
          <div className={styles.completeSection}>
            <CheckCircle size={48} color="#22c55e" />
            <h3>쿠폰 발송이 완료되었습니다</h3>
            <p>선택한 대상에게 쿠폰이 순차적으로 노출됩니다.</p>
            <button onClick={handleReset}>새 쿠폰 만들기</button>
          </div>
        </div>
      ) : (
        <div className={styles.contentLayout}>
          <main className={styles.mainColumn}>
            <section className={styles.sectionCard}>
              <h2 className={styles.cardTitle}>쿠폰 템플릿</h2>
              <div className={styles.templateGrid}>
                {templates.map(template => {
                  const target = templateTarget(template);
                  return (
                  <div
                    key={template.id}
                    className={`${styles.templateCard} ${activeTemplateId === template.id ? styles.selected : ''}`}
                    onClick={() => applyTemplate(template)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className={styles.templateHead}>
                      <span className={styles.headSpacer} aria-hidden="true" />
                      <span className={styles.templateTitle}>
                        <strong>{template.name}</strong>
                      </span>
                      <button aria-label={`${template.name} 더보기`} onClick={event => { event.stopPropagation(); setTemplateMenuId(templateMenuId === template.id ? null : template.id); }}><MoreHorizontal size={22} /></button>
                    </span>
                    <span className={styles.templateHighlight}>
                      <b>{templateDiscount(template)}</b>
                      {template.product_ids?.length > 0 && <small>상품 {template.product_ids.length}종</small>}
                    </span>
                    {(target || template.default_period_days) && (
                      <span className={styles.templateSpecs}>
                        {target && <span><em>대상</em>{target}</span>}
                        {template.default_period_days && <span><em>기간</em>{template.default_period_days}일</span>}
                      </span>
                    )}
                    {templateMenuId === template.id && <button className={styles.templateMore} disabled={deleteTemplate.isPending} onClick={event => { event.stopPropagation(); handleDeleteTemplate(template.id); }}><Trash2 size={18} />삭제하기</button>}
                  </div>
                  );
                })}
                <button className={styles.newTemplateCard} onClick={() => setIsTemplateModalOpen(true)}>
                  <Plus size={28} strokeWidth={1.5} />
                  <strong>새로운 쿠폰 템플릿 만들기</strong>
                </button>
              </div>
            </section>

            <section className={`${styles.sectionCard} ${targetMode === 'saved' && !editingSavedGroup ? styles.savedTargetSection : ''}`}>
              <h2 className={styles.cardTitle}><span className={styles.stepBadge}>1</span>타겟 그룹</h2>
              <div className={styles.segmented}>
                <button className={targetMode === 'new' ? styles.activeSegment : ''} onClick={() => setTargetMode('new')}>추천 타겟</button>
                <button className={targetMode === 'saved' ? styles.activeSegment : ''} onClick={() => setTargetMode('saved')}>내가 저장한 그룹 <span>{savedGroups.length}</span></button>
                <button className={targetMode === 'direct' ? styles.activeSegment : ''} onClick={() => setTargetMode('direct')}>직접 필터</button>
              </div>
              {targetMode === 'direct' ? (
                <div className={styles.directFilters}>
                  <h3>환자 노출 조건</h3>
                  <div><span>성별</span><span className={styles.chipRow}>{['전체', 'M', 'F'].map(g => <button key={g} className={gender === g ? styles.filterActive : ''} onClick={() => setGender(g)}>{genderLabel(g)}</button>)}</span></div>
                  <div><span>연령</span><span className={styles.chipRow}>{AGE_OPTIONS.map(age => <button key={age} className={(age === '전체' ? !selectedAges.length : selectedAges.includes(age)) ? styles.filterActive : ''} onClick={() => handleAgeToggle(age)}>{age}</button>)}</span></div>
                  <div className={styles.conditionFilters}><span>처방 질병</span><div>{conditionGroups.map(group => <section key={group.label}><small>{group.label}</small><p>{(group.items || []).map(item => <button key={item.code} className={selectedConditions.includes(item.code) ? styles.filterActive : ''} onClick={() => handleConditionToggle(item.code)}>{item.name || item.code}</button>)}</p></section>)}</div></div>
                  <div className={styles.targetSummary}>
                    <span className={styles.summaryHead}><img src="/images/event/alert-fill-blue.svg" alt="" /><b>선택된 타겟</b></span>
                    <span className={styles.summaryValues}>
                      <span>{genderLabel(gender)} 성별</span>
                      <span>{selectedAges.length ? selectedAges.join(', ') : '전체 연령'}</span>
                      <span>{selectedConditions.length ? selectedConditions.map(code => conditionNames[code] || code).join(', ') : '처방 질병 미선택'}</span>
                    </span>
                  </div>
                  <label className={styles.saveGroup}><input type="checkbox" /> 이 그룹을 저장하여 다음에도 사용</label>
                </div>
              ) : targetMode === 'saved' ? (
                <>
                <div className={styles.savedTargetGrid}>
                  {savedGroups.map(target => (
                    <div
                      key={target.id}
                      className={`${styles.savedTargetCard} ${selectedGroupId === target.id ? styles.selected : ''}`}
                      onClick={() => setSelectedGroupId(target.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <span><strong>{target.name}</strong><b>{target.count != null ? `${target.count}명` : '-'}</b><button aria-label={`${target.name} 더보기`} onClick={event => { event.stopPropagation(); setSavedMenuId(savedMenuId === target.id ? null : target.id); }}><MoreHorizontal size={22} /></button></span>
                      <small>{savedGroupDetail(target)}</small>
                      {conditionText(target) && <small className={styles.savedCondition}>{conditionText(target)}</small>}
                      {savedMenuId === target.id && (
                        <div className={styles.savedMore} onClick={event => event.stopPropagation()}>
                          <button disabled={deleteTargetGroup.isPending} onClick={() => handleDeleteSavedGroup(target.id)}><Trash2 size={18} />삭제하기</button>
                          <button onClick={() => startEditSavedGroup(target)}><SquarePen size={18} />편집하기</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {editingSavedGroup && (
                  <div className={`${styles.directFilters} ${styles.savedEditor}`}>
                    <h3>환자 노출 조건</h3>
                    <div><span>성별</span>{['전체', 'M', 'F'].map(g => <button key={g} className={gender === g ? styles.filterActive : ''} onClick={() => setGender(g)}>{genderLabel(g)}</button>)}</div>
                    <div><span>연령</span>{AGE_OPTIONS.map(age => <button key={age} className={(age === '전체' ? !selectedAges.length : selectedAges.includes(age)) ? styles.filterActive : ''} onClick={() => handleAgeToggle(age)}>{age}</button>)}</div>
                    <div className={styles.conditionFilters}><span>처방 질병</span><div>{conditionGroups.map(group => <section key={group.label}><small>{group.label}</small><p>{(group.items || []).map(item => <button key={item.code} className={selectedConditions.includes(item.code) ? styles.filterActive : ''} onClick={() => handleConditionToggle(item.code)}>{item.name || item.code}</button>)}</p></section>)}</div></div>
                    <footer className={styles.savedEditorFooter}><button onClick={() => setEditingSavedGroup(false)}>취소</button><button className={styles.primaryButton} disabled={saveTargetGroup.isPending} onClick={handleSaveEditedGroup}>변경사항 저장</button></footer>
                  </div>
                )}
                </>
              ) : (
                <div className={styles.targetGrid}>
                  {recommendedTargets.map(target => (
                    <button
                      key={target.key}
                      className={`${styles.targetCard} ${selectedGroupId === target.key ? styles.selected : ''}`}
                      onClick={() => setSelectedGroupId(target.key)}
                    >
                      <span><strong>{target.name}</strong><b>{target.count != null ? `${target.count}명` : '-'}</b></span>
                      <small>{target.detail}</small>
                      {conditionText(target.condition) && <small className={styles.targetCondition}>{conditionText(target.condition)}</small>}
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className={styles.sectionCard}>
              <h2 className={styles.cardTitle}><span className={styles.stepBadge}>2</span>상품 선택</h2>
              <div className={styles.recommendGrid}>
                <button className={productMode === 'trending' ? styles.selected : ''} onClick={() => setProductMode('trending')}><strong>전국 급상승 + 내 약국 보유</strong><span>-</span><small><Check size={16} /> 모두 재고 보유</small></button>
                <button className={productMode === 'bundle' ? styles.selected : ''} onClick={() => setProductMode('bundle')}><strong>함께 구매 묶음 (재고)</strong><span>-</span><small><Check size={16} /> 세트 구성 가능</small></button>
                <button className={productMode === 'new' ? styles.selected : ''} onClick={() => setProductMode('new')}><strong>신규 발주 추천 (미취급)</strong><span>-</span><small className={styles.warning}><AlertCircle size={16} /> 발주 필요</small></button>
              </div>
              <div className={styles.searchArea}>
                <label className={styles.searchBox}>
                  <Search size={20} />
                  <input value={productSearch} onFocus={() => setIsProductSearchOpen(true)} onChange={e => setProductSearch(e.target.value)} placeholder="상품명 또는 카테고리 검색..." />
                  <button type="button" aria-label="검색어 지우기" onClick={() => { setProductSearch(''); setIsProductSearchOpen(false); }}><X size={16} /></button>
                </label>
                {isProductSearchOpen && productSearch && (
                  <div className={styles.searchDropdown}>
                    {filteredProducts.slice(0, 3).map(product => <label key={product.id}><input type="checkbox" checked={selectedProducts.includes(product.id)} onChange={() => handleProductToggle(product.id)} /><span><b>{product.name}</b><small>{product.category}</small></span></label>)}
                    <footer><button onClick={() => setSelectedProducts(filteredProducts.map(product => product.id))}>전체 선택</button><button onClick={() => setSelectedProducts([])}>선택 해제</button></footer>
                  </div>
                )}
              </div>
              <div className={styles.productList}>
                {visibleProducts.map(product => (
                  <label key={product.id} className={`${selectedProducts.includes(product.id) ? styles.productSelected : ''} ${product.disabled ? styles.productDisabled : ''}`}>
                    <input type="checkbox" checked={selectedProducts.includes(product.id)} onChange={() => handleProductToggle(product.id)} />
                    <span className={styles.productName}><strong>{product.name}</strong><small>{product.rising && <em>급상승</em>}{product.category}</small></span>
                    <span className={styles.stock}>재고 보유<small>월 회전 {product.monthly ?? '-'}건</small></span>
                    <b>-</b>
                  </label>
                ))}
              </div>
              <div className={styles.selectionActions}>
                <button onClick={() => setSelectedProducts([])}>선택 해제</button>
                <button onClick={() => setSelectedProducts(visibleProducts.map(product => product.id))}>전체 선택</button>
              </div>
            </section>

            <section className={styles.sectionCard}>
              <h2 className={styles.cardTitle}><span className={styles.stepBadge}>3</span>쿠폰 조건</h2>
              <div className={styles.conditions}>
                <label><span>쿠폰명</span><input value={couponName} onChange={e => setCouponName(e.target.value)} placeholder={`예: ${THIS_MONTH} 신상품 프로모션`} maxLength={100} /></label>
                <label><span>쿠폰 안내문구</span><input value={description} onChange={e => setDescription(e.target.value)} placeholder="이번 달 떠오르는 인기 건기식을 10% 할인 받으세요!" /></label>
                <label><span>할인 방식</span><span className={styles.discountRow}><span className={styles.discountToggle}>{Object.entries(discountTypeLabel).map(([key, label]) => <button key={key} className={discountType === key ? styles.filterActive : ''} onClick={() => setDiscountType(key)}>{label}</button>)}</span><span className={styles.suffixInput}><input type="text" value={amount ? `${Number(amount).toLocaleString()}${discountType === 'PERCENT' ? '%' : '원'}` : ''} onChange={e => setAmount(e.target.value.replace(/\D/g, ''))} placeholder="3,000원" /></span></span></label>
                <div className={styles.dateRow}>
                  <label><span>시작일 <b>*</b></span><button type="button" className={styles.dateButton} onClick={() => setIsDateCalendarOpen(open => !open)}><span>{startDate}</span><CalendarDays size={18} /></button></label>
                  <label><span>종료일 <b>*</b></span><button type="button" className={styles.dateButton} onClick={() => setIsDateCalendarOpen(open => !open)}><span>{expiryDate}</span><CalendarDays size={18} /></button></label>
                  {isDateCalendarOpen && (
                    <div className={styles.dateCalendar}>
                      <Calendar
                        mode="range"
                        variant="event"
                        value={{ start: new Date(`${startDate}T00:00:00`), end: new Date(`${expiryDate}T00:00:00`) }}
                        minDate={new Date(`${getTodayString()}T00:00:00`)}
                        onClear={() => { setStartDate(getTodayString()); setExpiryDate(getNextMonthString()); }}
                        onClose={() => setIsDateCalendarOpen(false)}
                        onConfirm={() => setIsDateCalendarOpen(false)}
                        onChange={range => {
                          if (range.start) setStartDate(`${range.start.getFullYear()}-${String(range.start.getMonth() + 1).padStart(2, '0')}-${String(range.start.getDate()).padStart(2, '0')}`);
                          if (range.end) {
                            setExpiryDate(`${range.end.getFullYear()}-${String(range.end.getMonth() + 1).padStart(2, '0')}-${String(range.end.getDate()).padStart(2, '0')}`);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <label className={styles.saveTemplate}><input type="checkbox" checked={saveTemplate} onChange={e => setSaveTemplate(e.target.checked)} /><span>이 설정을 템플릿으로 저장</span><input disabled={!saveTemplate} value={saveTemplateName} onChange={e => setSaveTemplateName(e.target.value)} placeholder={`템플릿 이름 (예: ${THIS_MONTH} 정기 재활성화)`} /></label>
              </div>
            </section>
          </main>

          <aside className={styles.sideColumn}>
            <div className={styles.summaryCard}>
              <header><small>발송 대상</small><strong>{eligible?.count != null ? `${formatAmount(eligible.count)}명` : '-'}</strong>{selectedTarget && <span>{selectedTarget.name} 그룹</span>}</header>
              <div className={styles.summaryBody}>
                {(profileAge || profileTicket || eligible?.avg_days_since_last_visit != null) && (
                  <section><small>타겟 프로파일</small>
                    {(profileAge || profileTicket) && <p className={styles.summaryLine}>{profileAge && <span>{profileAge}</span>}{profileAge && profileTicket && <i>·</i>}{profileTicket && <span>{profileTicket}</span>}</p>}
                    {eligible?.avg_days_since_last_visit != null && <span>마지막 방문 - 평균 {eligible.avg_days_since_last_visit}일 전</span>}
                  </section>
                )}
                <section><small>선택 상품</small><p className={`${styles.summaryLine} ${styles.summaryAccent}`}><span>{selectedProductItems.length}종</span><i>·</i><span>총 재고 -</span><i>·</i><span>평균가 -</span></p>
                  <div className={styles.summaryProducts}>{selectedProductItems.slice(0, 3).map(product => (
                    <span key={product.id}>
                      {product.summaryName || product.name}
                      <b>보유</b>
                    </span>
                  ))}</div>
                  <div className={styles.summaryTotal}><span>{selectedProductItems.length}종</span><b>-</b></div>
                </section>
              </div>
              <footer>
                <button onClick={handleSubmit} disabled={isSubmitting || !isFormValid}>{isSubmitting ? '발송 중...' : '쿠폰 발송하기'}</button>
                <p className={styles.summaryNotice}><span>· 발송 후 최대 1일 내 환자에게 노출</span><span>· 앱 미사용자는 알림톡으로 자동 전환</span><span>· 발송 후 취소 시 사용 전 쿠폰만 회수 가능</span></p>
              </footer>
            </div>
          </aside>
        </div>
      )}
      {isTemplateModalOpen && (
        <div className={styles.modalOverlay} onMouseDown={() => setIsTemplateModalOpen(false)}>
          <div className={styles.templateModal} onMouseDown={event => event.stopPropagation()}>
            <header><span><FileText size={20} />새 템플릿 만들기</span><button onClick={() => setIsTemplateModalOpen(false)}><X size={16} /></button></header>
            <div className={styles.modalBody}>
              <label><span>템플릿 이름 <b>*</b></span><input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="예: 갱년기 여성 단골 케어" /></label>
              <label className={styles.modalSelect}><span>대상 그룹</span><button className={styles.selectField} onClick={() => setModalDropdown(modalDropdown === 'target' ? null : 'target')}>{modalTarget ? targetOptionLabel(modalTarget) : modalDirect ? '직접 필터로 설정' : '추천 타겟에서 선택'}<ChevronDown size={18} /></button>{modalDropdown === 'target' && (
                  <div className={styles.modalDropdown}>
                    <span className={styles.modalDropdownHead}>추천 타겟에서 선택</span>
                    {recommendedTargets.map(target => (
                      <button key={target.key} onClick={() => { setModalTargetKey(target.key); setModalDirect(false); setModalDropdown(null); }}>
                        {targetOptionLabel(target)}
                      </button>
                    ))}
                    <button className={styles.modalDropdownFoot} onClick={() => { setModalTargetKey(null); setModalDirect(true); setModalDropdown(null); }}>— 직접 필터로 설정 —</button>
                  </div>
                )}</label>
              <label className={styles.modalProducts}><span>적용 상품</span>
                <input value={modalProductSearch} onChange={e => setModalProductSearch(e.target.value)} placeholder="상품명 또는 바코드 검색" />
                {modalProductSearch.trim() && modalProductResults.length > 0 && (
                  <div className={styles.modalProductList}>
                    {modalProductResults.map(p => (
                      <button type="button" key={p.barcode} className={modalProducts.includes(p.barcode) ? styles.modalProductOn : ''}
                        onClick={() => setModalProducts(prev => prev.includes(p.barcode) ? prev.filter(b => b !== p.barcode) : [...prev, p.barcode])}>
                        <b>{p.name}</b><small>{[p.manufacturer, p.product_type].filter(Boolean).join(' · ')}</small>
                      </button>
                    ))}
                  </div>
                )}
                {modalProducts.length > 0 && (
                  <span className={styles.modalProductChips}>
                    {modalProducts.map(b => (
                      <em key={b}>{seenProducts.current[b]?.name || b}<button type="button" aria-label="상품 빼기" onClick={() => setModalProducts(prev => prev.filter(x => x !== b))}><X size={12} /></button></em>
                    ))}
                  </span>
                )}
              </label>
              <div className={styles.modalRow}><label className={styles.modalSelect}><span>할인 방식</span><button className={styles.selectField} onClick={() => setModalDropdown(modalDropdown === 'discount' ? null : 'discount')}>{MODAL_DISCOUNT_LABEL[modalDiscount]}<ChevronDown size={18} /></button>{modalDropdown === 'discount' && <div className={styles.modalDropdown}>{Object.entries(MODAL_DISCOUNT_LABEL).map(([key, label]) => <button key={key} onClick={() => { setModalDiscount(key); setModalDropdown(null); }}>{label}</button>)}</div>}</label><label><span>할인 값</span><input type="number" min="1" value={modalDiscountValue} onChange={e => setModalDiscountValue(e.target.value)} placeholder="예: 3000" /></label></div>
              <label><span>기본 유효기간 (일)</span><input type="number" min="1" max="365" value={modalPeriod} onChange={e => setModalPeriod(e.target.value)} /></label>
              <label><span>쿠폰 안내문구 (선택)</span><textarea value={modalGuide} onChange={e => setModalGuide(e.target.value)} placeholder="환자에게 표시될 기본 문구. 발송 시 수정 가능." /></label>
            </div>
            <footer><button onClick={() => setIsTemplateModalOpen(false)}>취소</button><button className={styles.primaryButton} disabled={!isModalValid || createTemplate.isPending} onClick={handleCreateTemplate}>템플릿 저장</button></footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponDistribute;
